// ============================================================
// Asesor.jsx — Asesor inline del Centro de Aplicaciones
// La conversación aparece en la propia página, bajo el buscador,
// sin panel flotante ni tarjeta. Estilo limpio tipo informe.
// ============================================================
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { sendMessage } from './chatService';
import { generateReportFromSpec, generateReportFromMarkdown } from './pdfReports';
// Sistema de diseño de la suite: la paleta de aquí pasa a ser un alias de los
// tokens de `src/ui/theme.js`, para que el asesor se vea como el resto.
import { C } from '../../ui/theme.js';

const palette = {
  primary: C.brand,
  accent: '#1a9cd4',
  border: C.line,
  text: C.ink,
  textSoft: C.page,
  user: C.brand,
};

const EJEMPLOS = [
  '¿Cuántos leads hemos recibido este mes y cuántos se han vendido?',
  'Ranking de comerciales por leads y ventas este mes',
  'Leads por marca este mes (top 5)',
  '¿Cuántos leads pendientes hay en el inbox de AutoLeads?',
  'Resumen de costes de servicios y alertas de presupuesto',
  'Hazme un informe en PDF del rendimiento comercial del mes',
];

// ---------- Markdown ligero (con tablas) ----------
function renderInline(text) {
  let html = (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#005187;text-decoration:underline;">$1</a>',
  );
  html = html.replace(
    /`([^`]+)`/g,
    '<code style="background:#f4f5f7;padding:1px 8px;border-radius:999px;font-family:Menlo,Consolas,monospace;font-size:12.5px;">$1</code>',
  );
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(^|\s)\*([^*\n]+)\*/g, '$1<em>$2</em>');
  html = html.replace(/(^|\s)_([^_\n]+)_/g, '$1<em>$2</em>');
  return html;
}

function cellAlign(s) {
  const t = (s || '').trim();
  return /[0-9]/.test(t) && /^[\s0-9.,%+\-()$€]+$/.test(t.replace(/EUR/g, '')) ? 'right' : 'left';
}
function splitRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}
function isTableSep(line) {
  return line.includes('-') && /^\s*\|?[\s:|-]+\|?\s*$/.test(line);
}
function tableToHtml(rows) {
  const head = rows[0] || [];
  const body = rows.slice(1);
  // Tabla con la forma del sistema: cabecera gris clara en versalitas y celdas
  // de 12,5 px separadas por una línea fina.
  let h = '<table style="border-collapse:collapse;width:100%;margin:10px 0;border:1px solid #e7e9ec;border-radius:10px;overflow:hidden;">';
  h += '<thead><tr>' + head.map((c) => `<th style="text-align:left;background:#f7f8fa;color:#8a929c;padding:8px 10px;font-size:10.5px;font-weight:800;letter-spacing:0.3px;text-transform:uppercase;border-bottom:1.5px solid #e7e9ec;">${renderInline(c)}</th>`).join('') + '</tr></thead><tbody>';
  body.forEach((r) => {
    h += '<tr>' + r.map((c) => `<td style="padding:8px 10px;border-bottom:1px solid #eaecef;text-align:${cellAlign(c)};font-size:12.5px;color:#2b333d;">${renderInline(c)}</td>`).join('') + '</tr>';
  });
  h += '</tbody></table>';
  return h;
}
function blocksToHtml(seg) {
  const lines = seg.split('\n');
  let out = '';
  let buffer = [];
  const flush = () => {
    if (!buffer.length) return;
    out += buffer
      .map((line) => {
        if (/^### /.test(line)) return `<div style="margin:10px 0 4px;font-size:14px;font-weight:700;color:#1f2a37;">${renderInline(line.slice(4))}</div>`;
        if (/^## /.test(line)) return `<div style="margin:12px 0 5px;font-size:15.5px;font-weight:700;color:#1f2a37;">${renderInline(line.slice(3))}</div>`;
        if (/^# /.test(line)) return `<div style="margin:14px 0 6px;font-size:17px;font-weight:700;color:#1f2a37;">${renderInline(line.slice(2))}</div>`;
        if (/^\s*[-*]\s+/.test(line)) return `<div style="padding-left:16px;position:relative;margin:3px 0;"><span style="position:absolute;left:2px;color:#1a9cd4;">•</span>${renderInline(line.replace(/^\s*[-*]\s+/, ''))}</div>`;
        if (/^\s*\d+\.\s+/.test(line)) return `<div style="padding-left:18px;margin:3px 0;">${renderInline(line.trim())}</div>`;
        if (line.trim() === '') return '<div style="height:7px;"></div>';
        return `<div style="margin:3px 0;">${renderInline(line)}</div>`;
      })
      .join('');
    buffer = [];
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim().startsWith('|') && line.includes('|')) {
      flush();
      const tbl = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        if (!isTableSep(lines[i])) tbl.push(splitRow(lines[i]));
        i += 1;
      }
      if (tbl.length) out += tableToHtml(tbl);
      continue;
    }
    buffer.push(line);
    i += 1;
  }
  flush();
  return out;
}

function MarkdownText({ text }) {
  const parts = [];
  const segs = (text || '').split(/```([\s\S]*?)```/g);
  segs.forEach((seg, i) => {
    if (i % 2 === 1) {
      const firstLine = seg.split('\n')[0] || '';
      const body = seg.startsWith(firstLine + '\n') ? seg.slice(firstLine.length + 1) : seg;
      parts.push(
        <pre key={'c' + i} style={{ background: C.ink, color: C.line, padding: 12, borderRadius: 12, fontSize: 12.5, lineHeight: 1.5, overflowX: 'auto', margin: '6px 0', fontFamily: 'Menlo,Consolas,monospace' }}>
          {body || firstLine}
        </pre>,
      );
    } else {
      const html = blocksToHtml(seg);
      // eslint-disable-next-line react/no-danger
      parts.push(<span key={'t' + i} dangerouslySetInnerHTML={{ __html: html }} />);
    }
  });
  return <>{parts}</>;
}

// ---------- Pie: Fuentes + Cómo lo he averiguado + PDF ----------
function SourceFooter({ msg }) {
  const [howOpen, setHowOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const meta = msg.meta;
  const fuentes = (meta && meta.fuentes) || [];
  const queries = (meta && meta.queries) || [];

  const downloadPdf = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const att = msg.reportSpec
        ? await generateReportFromSpec(msg.reportSpec)
        : await generateReportFromMarkdown(msg.fullText || msg.content, null, meta);
      const a = document.createElement('a');
      a.href = att.url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      window.alert('No pude generar el PDF: ' + (e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${C.line}` }}>
      {fuentes.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: C.muted }}>Fuentes:</span>
          {fuentes.map((f, i) => (
            <span key={i} style={{ fontSize: 10.5, background: `${C.brand}12`, color: C.brand, border: `1px solid ${C.brand}33`, borderRadius: 999, padding: '3px 10px', fontWeight: 800, letterSpacing: 0.3 }}>{f}</span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <button style={{ background: 'none', border: 'none', color: C.muted, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, cursor: 'pointer', padding: 0, textTransform: 'uppercase' }} type="button" onClick={() => setHowOpen((v) => !v)}>
          {howOpen ? '▾' : '▸'} Cómo lo he averiguado
          {queries.length > 0 ? '  ·  ' + queries.length + ' consulta' + (queries.length === 1 ? '' : 's') + ' de solo lectura' : ''}
        </button>
        <button className="fg-nav" style={{ background: '#fff', border: `1px solid ${C.line}`, color: C.page, borderRadius: 10, padding: '7px 14px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }} type="button" onClick={downloadPdf} disabled={busy}>
          {busy ? 'Generando…' : '⬇ Descargar PDF'}
        </button>
      </div>
      {howOpen && (
        <div style={{ marginTop: 8, background: C.bg, border: `1px solid ${C.line}`, borderRadius: 12, padding: '10px 12px', fontSize: 12, color: C.page, lineHeight: 1.6 }}>
          {queries.length === 0 && <div>Respuesta directa, sin consultas a la base de datos.</div>}
          {queries.map((q, i) => (
            <div key={i}>· {q.fuente} — {String(q.detalle || '').replace(/sobre consulta$/, '(consulta SQL)')}</div>
          ))}
          {meta && meta.snapshot && <div style={{ marginTop: 4, fontStyle: 'italic', color: C.faint }}>Datos de {meta.snapshot} · acceso de solo lectura</div>}
        </div>
      )}
    </div>
  );
}

// ---------- Componente principal ----------
export default function Asesor() {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const handleSend = useCallback(
    async (text) => {
      const userText = (text || '').trim();
      if (!userText || streaming) return;
      const userMsg = { role: 'user', content: userText };
      setMessages((prev) => [...prev, userMsg, { role: 'assistant', content: '' }]);
      setDraft('');
      setStreaming(true);
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const onChunk = (piece) => {
        setMessages((prev) => {
          const next = prev.slice();
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') next[next.length - 1] = { ...last, content: last.content + piece };
          return next;
        });
      };
      try {
        const result = await sendMessage([...messages, userMsg], onChunk, ctrl.signal);
        if (result && result.mode !== 'aborted') {
          setMessages((prev) => {
            const next = prev.slice();
            const last = next[next.length - 1];
            if (last && last.role === 'assistant') {
              next[next.length - 1] = { ...last, meta: result.meta || null, fullText: result.fullText || last.content, reportSpec: result.reportSpec || null };
            }
            return next;
          });
        }
      } catch (_e) {
        // error duro o abortado
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [messages, streaming],
  );

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(draft);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: 850, margin: '0 auto' }}>
      {/* Buscador */}
      <div style={{ display: 'flex', gap: 12 }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder="Pregunta sobre leads, campañas, ventas… (Enter para enviar)"
          rows={1}
          disabled={streaming}
          style={{ flex: 1, padding: '14px 16px', fontSize: 13.5, border: `1.5px solid ${C.line}`, borderRadius: 10, outline: 'none', background: C.bg, color: C.ink, resize: 'none', fontFamily: 'inherit', lineHeight: 1.4 }}
        />
        <button
          type="button"
          onClick={() => (streaming ? abortRef.current && abortRef.current.abort() : handleSend(draft))}
          style={{ padding: '0 30px', background: streaming ? C.rojo : C.brand, border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', boxShadow: streaming ? '0 3px 10px rgba(210,59,52,.28)' : '0 3px 10px rgba(0,81,135,.28)' }}
        >
          {streaming ? 'Detener' : 'Enviar'}
        </button>
      </div>

      {/* Ejemplos + disclaimer */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 10, gap: 4 }}>
        <details style={{ position: 'relative' }}>
          <summary style={{ listStyle: 'none', cursor: 'pointer', color: C.brand, fontSize: 12.5, fontWeight: 800 }}>
            ¿Qué puedo preguntarte? ▾
          </summary>
          {/* Los ejemplos van en píldoras, como los filtros del resto de la suite. */}
          <div style={{ position: 'absolute', top: '120%', left: '50%', transform: 'translateX(-50%)', width: 'min(560px, 86vw)', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: '0 14px 34px rgba(15,42,70,0.14)', padding: 10, zIndex: 20, display: 'flex', flexWrap: 'wrap', gap: 7, justifyContent: 'center' }}>
            {EJEMPLOS.map((ex, i) => (
              <button key={i} type="button" disabled={streaming} onClick={() => handleSend(ex)} className="fg-nav" style={{ textAlign: 'left', background: '#fff', border: `1px solid ${C.line}`, padding: '6px 13px', fontSize: 12, fontWeight: 700, color: C.page, cursor: 'pointer', borderRadius: 999 }}>
                {ex}
              </button>
            ))}
          </div>
        </details>
        <div style={{ fontSize: 11, color: C.faint, textAlign: 'center' }}>
          El asesor lee el CRM y Supabase en solo lectura · puede equivocarse, verifica las cifras críticas.
        </div>
      </div>

      {/* Conversación inline (sin tarjeta) */}
      <div style={{ marginTop: 24, textAlign: 'left' }}>
        {messages.map((m, i) => {
          if (m.role === 'user') {
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'flex-end', margin: '18px 0 10px' }}>
                <div style={{ background: palette.user, color: '#fff', padding: '10px 16px', borderRadius: 12, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, maxWidth: '85%', boxShadow: '0 3px 10px rgba(0,81,135,.20)' }}>
                  {m.content}
                </div>
              </div>
            );
          }
          const isLast = i === messages.length - 1;
          return (
            <div key={i} style={{ borderTop: `1px solid ${C.line}`, paddingTop: 14, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span className="fg-breath" style={{ width: 8, height: 8, borderRadius: '50%', background: C.verde }} />
                <span style={{ fontSize: 12.5, fontWeight: 800, color: C.brand }}>Asesor Dimoauto</span>
              </div>
              <div style={{ fontSize: 13.5, color: palette.text, lineHeight: 1.6 }}>
                <MarkdownText text={m.content} />
                {streaming && isLast && (
                  <span style={{ display: 'inline-block', width: 7, height: 15, background: palette.accent, marginLeft: 2, verticalAlign: 'text-bottom', animation: 'dimoBlink 1s steps(2) infinite' }} />
                )}
              </div>
              {!(streaming && isLast) && m.content && <SourceFooter msg={m} />}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <style>{'@keyframes dimoBlink{0%,100%{opacity:1}50%{opacity:0}} details>summary::-webkit-details-marker{display:none}'}</style>
    </div>
  );
}
